import {useMemo} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {useProducts} from '@amblique/king-living/app/hooks/use-products';

import {
  Box,
  Flex,
  Image,
  Skeleton,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {PIM_SQUARE_THUMBNAIL_PX, resolveProductThumbnailUrl} from '@amblique/king-living/app/components/configuration-selector/utils';
import {GET_PRODUCTS_PARAMS} from '@amblique/king-living/app/utils/api-utils';
import {getOptionsGroupingUIProcessed, resolveShelvesPrimaryName, trimProductNameLine} from '@amblique/king-living/app/utils/product-utils';

function isCushionContainerId(containerId) {
  const normalized = String(containerId || '').toLowerCase();

  return normalized === 'cushion' || normalized === 'cushions';
}

/**
 * Finished-good simple PDP — shelves row copy (Figma 7730:69145 / 7730:68762).
 * Line 1 (slot label): recommendationType.displayValue;
 * line 2 (primary name): nameLine2 + nameLine1 group, then fallback name.
 */
function resolveFinishedGoodShelvesLines(displayValue, product) {
  const slotLabel = displayValue?.trim() || null;
  const primaryName = resolveShelvesPrimaryName(product);

  return {slotLabel, primaryName};
}

/**
 * Finished-good simple PDP — cushion row copy.
 * Line 1: nameLine1, or name when both nameLine1 and nameLine2 are absent;
 * line 2: shortDescription.
 */
function resolveFinishedGoodCushionLines(product) {
  const line1 = trimProductNameLine(product?.c_nameLine1);
  const line2 = trimProductNameLine(product?.c_nameLine2);
  const title = line1 || ((!line1 && !line2) ? (trimProductNameLine(product?.name) || null) : null);
  const description =
    (typeof product?.shortDescription === 'string' ? product.shortDescription.trim() : '') ||
    (typeof product?.c_shortDescription === 'string' ? product.c_shortDescription.trim() : '') ||
    null;

  return {title, description};
}

/**
 * Read-only "what's included" list for a Finished Good (TSD - PDP Simple §7 / KINDW-1123).
 *
 * Bundled IDs and recommendation mapping from the finished good's SCAPI response;
 * display structure from master `c_optionsGroupingUIProcessed` (via `masterProduct`):
 *
 *   - Master `c_optionsGroupingUIProcessed` — container / slot structure (required)
 *   - `recommendations[]` (SCAPI `expand=recommendations`) — `recommendedItemId` ↔
 *     `recommendationType.value` mapping. Lets us figure out which bundled product
 *     belongs in which section.
 *   - `c_selectedOptions` — the bundled product IDs.
 *
 * Pipeline:
 *   1. For each container in master OGU, walk **every** entry in its
 *      `values[]` and resolve the bundled product whose `recommendationType.value` matches
 *      that entry's `recommendationTypeId`. A container can group multiple recommendation
 *      types under one heading (e.g. "Shelves" with recTypes 11 + 12 → two shelves), so
 *      the result is N rows under one heading rather than one row per container. Each
 *      resolved item also carries the matching recommendation's `displayValue` (e.g.
 *      "Timber Veneer Shelf 1: 104cm") so the row can label which slot the product fills.
 *   2. Batch-fetch all matched product IDs via `useProducts` (one request, React Query
 *      cached).
 *   3. Render one heading per container + one row per matched product. Shelves vs cushion
 *      use different 2-line copy rules and typography. Shelves: slot label (displayValue)
 *      above product name — Figma 7730:69145 / 7730:68762. Cushion: title above
 *      shortDescription — Figma 7730:69205 / 7730:68783.
 *
 * Why this rather than `<CustomizeStepsInline readOnly>`:
 *   - We don't need the customize journey context, the Done/Subtotal footer, the cover
 *     step, the sub-view picker, step locking, or any of the master-OGU-shaped data
 *     scaffolding.
 *   - Reuses `<RadioOptionContent>` for visual parity with the cover row and the
 *     interactive option rows on Complex / interactive Simple PDPs.
 *
 * @param {Object} props
 * @param {Object} props.product - Finished good product (SCAPI response with
 *   `recommendations` expanded — required).
 * @param {Object} [props.masterProduct] - Master with `c_optionsGroupingUIProcessed`.
 */
export function FinishedGoodOptionsList({product, masterProduct}) {
  const styles = useMultiStyleConfig('FinishedGoodView', {});

  // Map `recommendedItemId` → `{typeValue, displayValue}` from the finished good's own
  // recommendations. SCAPI camelCase shape: `recommendationType.value` (number id) and
  // `recommendationType.displayValue` (the slot label, e.g. "Cushion",
  // "Timber Veneer Shelf 1: 104cm").
  // Hidden recommendations (`c_isHidden === true`, e.g. legs that are baked into the
  // bundle but shouldn't show up in the "what's included" list) are excluded outright
  // — without an entry in this map, the matching pass in `sections` below can't pair
  // their productId with any container, so they fall through silently.
  const recByProductId = useMemo(() => {
    const map = new Map();
    const recs = Array.isArray(product?.recommendations) ? product.recommendations : [];

    recs.forEach((rec) => {
      if (rec?.c_isHidden === true) return;

      const id = rec?.recommendedItemId;
      const typeValue = rec?.recommendationType?.value;

      if (id != null && typeValue != null) {
        map.set(id, {
          typeValue,
          displayValue: rec?.recommendationType?.displayValue ?? null,
        });
      }
    });

    return map;
  }, [product?.recommendations]);

  const sections = useMemo(() => {
    const containers = getOptionsGroupingUIProcessed(masterProduct) || [];
    const selectedIds = Array.isArray(product?.c_selectedOptions) ? product.c_selectedOptions : [];
    const usedIds = new Set();

    return containers
      .map((container) => {
        const cid = container?.containerId;

        if (!cid || cid === 'cover') return null;

        if (container.isHidden === true || container.isVisible === false) return null;

        const values = Array.isArray(container?.values) ? container.values : [];

        const items = values
          .map((value) => {
            const wantTypeId = value?.recommendationTypeId;

            if (wantTypeId == null) return null;

            // Pick the first selectedId that matches this recType AND hasn't already
            // been consumed by a previous slot in this (or another) container. Without
            // the dedupe, multiple recTypes that resolve to the same product would
            // double-render the same row.
            const matchedId = selectedIds.find(
              (id) => !usedIds.has(id) && recByProductId.get(id)?.typeValue === wantTypeId
            );

            if (!matchedId) return null;

            usedIds.add(matchedId);

            return {
              productId: matchedId,
              recommendationTypeId: wantTypeId,
              displayValue: recByProductId.get(matchedId)?.displayValue ?? null,
            };
          })
          .filter(Boolean);

        if (items.length === 0) return null;

        return {
          containerId: cid,
          containerName: container.containerName || null,
          items,
        };
      })
      .filter(Boolean);
  }, [masterProduct, product, recByProductId]);

  // De-duplicate ids across all containers/items for the batch fetch. SCAPI accepts up
  // to 24 ids per call; finished goods have at most a handful of bundled options, so a
  // single batch call is enough.
  const ids = useMemo(
    () => {
      const all = sections.flatMap((s) => s.items.map((i) => i.productId));

      return Array.from(new Set(all)).join(',');
    },
    [sections]
  );

  const {data: productsData, isLoading} = useProducts(
    {
      parameters: {
        ...GET_PRODUCTS_PARAMS,
        ids,
      },
    },
    {enabled: Boolean(ids), staleTime: 5 * 60 * 1000}
  );

  const productById = useMemo(() => {
    const map = new Map();

    (productsData?.data || []).forEach((p) => {
      if (p?.id) map.set(p.id, p);
    });

    return map;
  }, [productsData]);

  if (sections.length === 0) return null;

  if (isLoading) {
    return (
      <Box className="finished-good-options-list" {...styles.optionsList}>
        {sections.map((s) => (
          <Box key={s.containerId} {...styles.section}>
            <Text as="p" {...styles.sectionHeading}>
              {s.containerName || (
                <FormattedMessage
                  defaultMessage="Included"
                  id="finished_good.options_list.included_fallback"
                />
              )}
            </Text>
            <Box {...styles.itemList}>
              {s.items.map((i) => (
                <Skeleton key={i.productId} height="87px" />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box className="finished-good-options-list" {...styles.optionsList}>
      {sections.map((s) => {
        const renderableItems = s.items
          .map((i) => ({...i, fetched: productById.get(i.productId)}))
          .filter((i) => i.fetched);

        if (renderableItems.length === 0) return null;

        const isCushionContainer = isCushionContainerId(s.containerId);
        const itemStyle = isCushionContainer ? styles.cushionItem : styles.optionItem;

        return (
          <Box key={s.containerId} {...styles.section}>
            {s.containerName && (
              <Text as="p" {...styles.sectionHeading}>
                {s.containerName}
              </Text>
            )}
            <Box {...styles.itemList}>
              {renderableItems.map((i) => {
                const imageUrl = resolveProductThumbnailUrl(i.fetched);

                if (isCushionContainer) {
                  const {title, description} = resolveFinishedGoodCushionLines(i.fetched);
                  const imageAlt = title || description || i.fetched.name;

                  return (
                    <Box
                      key={i.productId}
                      className="finished-good-options-list__item"
                      {...itemStyle}
                    >
                      <Flex className="finished-good-options-list__row" {...styles.itemRow}>
                        {imageUrl && (
                          <Image
                            alt={imageAlt}
                            className="finished-good-options-list__image"
                            htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
                            htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
                            loading="lazy"
                            src={imageUrl}
                            {...styles.cushionItemImage}
                          />
                        )}
                        <Flex className="finished-good-options-list__text" {...styles.itemText}>
                          {title && (
                            <Text
                              className="finished-good-options-list__item-title"
                              {...styles.optionItemTitle}
                            >
                              {title}
                            </Text>
                          )}
                          {description && (
                            <Text
                              className="finished-good-options-list__item-description"
                              {...styles.optionItemDescription}
                            >
                              {description}
                            </Text>
                          )}
                        </Flex>
                      </Flex>
                    </Box>
                  );
                }

                const {slotLabel, primaryName} = resolveFinishedGoodShelvesLines(
                  i.displayValue,
                  i.fetched
                );
                const imageAlt = primaryName || slotLabel || i.fetched.name;

                return (
                  <Box
                    key={i.productId}
                    className="finished-good-options-list__item"
                    {...itemStyle}
                  >
                    <Flex className="finished-good-options-list__row" {...styles.itemRow}>
                      {imageUrl && (
                        <Image
                          alt={imageAlt}
                          className="finished-good-options-list__image"
                          htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
                          htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
                          loading="lazy"
                          src={imageUrl}
                          {...styles.optionItemImage}
                        />
                      )}
                      <Flex className="finished-good-options-list__text" {...styles.itemText}>
                        {slotLabel && (
                          <Text
                            className="finished-good-options-list__slot-label"
                            {...styles.shelvesSlotLabel}
                          >
                            {slotLabel}
                          </Text>
                        )}
                        {primaryName && (
                          <Text
                            className="finished-good-options-list__product-name"
                            {...styles.shelvesPrimaryName}
                          >
                            {primaryName}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

FinishedGoodOptionsList.displayName = 'FinishedGoodOptionsList';

FinishedGoodOptionsList.propTypes = {
  product: PropTypes.object.isRequired,
  masterProduct: PropTypes.object,
};
